import { useCallback, useEffect, useRef, useState } from 'react';
import { notificaErrore } from './notifiche';
import { campoSalvatoSenzaUscire, uscitaSenzaSalvareConfermata } from './uscitaSicura';

/**
 * salvaCampo — il salvataggio automatico, un campo per volta.
 *
 * QUANDO IL MEDICO ESCE DA UN CAMPO, quel campo si salva. Niente bozza:
 * su una cartella clinica quello che e' stato scritto e' scritto, e se
 * il browser muore a meta' visita non si perde niente. Il prezzo -
 * anche un refuso resta in cartella - e' stato accettato il 22 settembre
 * 2026: l'audit tiene lo storico per campo e si vede cosa e' cambiato.
 *
 * TRE STATI, E UNO NON SPARISCE MAI DA SOLO:
 *   in_corso     sta andando;
 *   salvato      andato: la spunta sbiadisce dopo qualche secondo,
 *                perche' un successo che resta a schermo diventa rumore;
 *   non_salvato  NON sbiadisce. Un medico che perde la rete per un
 *                minuto deve poter tornare indietro e vedere quali
 *                campi non sono passati: un avviso che sparisce dopo
 *                tre secondi non glielo dice.
 *
 * NESSUN RITENTATIVO AUTOMATICO. Un ritentativo che riscrive da solo in
 * una cartella clinica e' una scrittura che nessuno ha chiesto. Si
 * riprova cliccando, ed e' un gesto di una persona.
 *
 * UNA CODA PER CAMPO, non un numero di versione. Se una richiesta per
 * quel campo e' gia' in volo, la seconda non parte in parallelo:
 * aspetta, e se nel frattempo il testo cambia ancora si manda SOLO
 * l'ultimo valore. Cosi' due modifiche rapide sullo stesso campo fanno
 * una scrittura sola, con il valore giusto, e due risposte non possono
 * tornare invertite. Il numero di versione (una colonna in piu', 409 se
 * qualcuno ha scritto nel frattempo) serve quando due medici scrivono
 * lo stesso campo nello stesso minuto: si aggiunge il giorno che
 * succede.
 *
 * IL TESTO NON SALVATO NON VA IN `localStorage`, NE' IN
 * `sessionStorage`, NE' IN IndexedDB. Sarebbe la soluzione ovvia per non
 * perderlo a un ricaricamento, e non si fa: le postazioni della clinica
 * sono condivise, e quel testo e' una riga di cartella clinica che
 * resterebbe sul disco di quel computer, leggibile dal prossimo che si
 * siede li', fuori da ogni permesso e da ogni traccia. Conseguenza
 * accettata: ricaricando la pagina con dei campi rossi, quel testo si
 * perde. La protezione e' l'avviso in uscita, che c'e' gia'.
 *
 * COSA SI E' AGGIUNTO IL 14 OTTOBRE 2026 (richiesta di Alex: «non e' chiaro se
 * una riga nuova degli interventi si salva senza premere Aggiungi»):
 *
 *   - `salvatoAlle`: l'ORA dell'ultimo salvataggio riuscito di ogni campo, che
 *     NON sbiadisce come la spunta. Serve a `StatoSalvataggio`, il «Salvato ✓
 *     14:32» che resta a schermo: un successo che sparisce in tre secondi non
 *     dice a chi torna dopo un minuto se il campo e' passato;
 *   - `segnaBase(campo, valore)`: il valore che c'e' in archivio (dopo la
 *     lettura). Un campo che esce com'e' non si scrive e non dice «salvato»:
 *     «un campo che non e' cambiato non si salva» valeva per il server, ora
 *     anche per il browser. Senza base (campo mai letto) si scrive come prima;
 *   - `allaChiusura`: quando il riquadro SI SMONTA (cambio di linguetta della
 *     cartella, Ctrl K, indietro) il campo che stava avendo il fuoco non esce
 *     da solo - nessun blur - e il testo si perdeva. Si chiama con le funzioni
 *     di salvataggio di sempre, con le stesse regole; scrivono SOLO i campi
 *     che hanno una base e sono diversi da lei: un riquadro non ancora letto
 *     non scrive niente (scriverebbe vuoto sopra il dato). Se la scrittura
 *     fallisce a schermata chiusa non c'e' piu' un segno rosso a cui
 *     tornare: lo dice una notifica.
 *
 *   - `uscita` (14 ottobre 2026, decisione di Alex): uscendo da una schermata
 *     con un campo scritto PRIMA SI SALVA, e la domanda «modifiche non
 *     salvate» compare solo se il salvataggio fallisce o non finisce in
 *     tempo - mai quando va a buon fine, nemmeno con la richiesta ancora in
 *     volo. `uscita.salvaPrima()` manda quello che c'e' da mandare, aspetta le
 *     richieste in volo e risolve true se e' andato tutto; `uscita.nonMandato()`
 *     dice se c'e' del testo scritto che non e' nemmeno partito (un
 *     salvataggio fallito, un valore che non puo' partire): solo allora il
 *     browser avvisa alla chiusura. Si passano a `useUscitaProtetta`.
 *
 * @param {Function} scrivi     (campo, valore) => Promise. Una scrittura.
 * @param {Function} onSalvato  chiamata dopo ogni scrittura riuscita: serve a
 *        chi tiene un'istantanea del modulo per sapere che quel pezzo e'
 *        in archivio, e non dire piu' «modifiche non salvate» per tutto.
 * @param {Object}   [opzioni]
 * @param {Function} [opzioni.allaChiusura]  chiamata allo smontaggio e prima di
 *        uscire, con le funzioni dell'ultimo render; se torna false c'e' un
 *        valore che non puo' partire (una data scritta male).
 * @param {Function} [opzioni.bloccato]  () => true se c'e' un valore che non
 *        puo' partire.
 */
/* «Chiudi» con un campo che NON e' passato: chiudere fa sparire la casella
   e il testo che ci era rimasto. Si chiede, e si dice che cosa si perde
   (24 settembre 2026, dopo la verifica chiesta da Alex). Una frase sola per
   tutte le «Chiudi» dei campi che si salvano da soli. */
export const MSG_CHIUDI_NON_SALVATO = 'Un campo non è stato salvato: se chiudi, il testo che non è passato si perde. '
    + 'Chiudere lo stesso?';

/** Si puo' chiudere? Mai con un salvataggio in corso; con un campo rosso, solo se si conferma. */
export function sipuoChiudere(inCorso, nonSalvati) {
    if (inCorso) return false;
    if (nonSalvati > 0) return window.confirm(MSG_CHIUDI_NON_SALVATO);
    return true;
}

/* Il valore come lo confronta la base: '' e null sono lo stesso «vuoto»
   (i campi di testo salvano `valore || null`). */
const impronta = (v) => JSON.stringify(v === '' || v === undefined ? null : v);

/** Il gestore di «Invio» per un campo: salva come l'uscita dal campo, senza
    spostare il fuoco (Alex, 14/10/2026). Vale anche per una textarea
    («Modifica voce»): Invio salva e NON va a capo. Maiusc+Invio non salva e
    non si intromette: il browser fa il suo gesto, e in una textarea inserisce
    l'a capo (in un campo a riga singola non fa niente). Non si intromette
    nemmeno con la composizione di una parola (tastiere dei tablet che
    riscrivono il testo mentre lo si compone). */
export const conInvio = (salva) => (e) => {
    if (e.key !== 'Enter' || e.nativeEvent?.isComposing || e.shiftKey) return;
    e.preventDefault();
    salva(e);
    /* Il testo e' in archivio: non c'e' piu' niente di scritto e non uscito,
       e il browser non deve avvisare alla chiusura. */
    campoSalvatoSenzaUscire();
};

export function useSalvaCampo(scrivi, onSalvato, opzioni = {}) {
    const [stato, setStato] = useState({});      // campo -> 'in_corso' | 'salvato' | 'non_salvato'
    const inVolo = useRef({});                   // campo -> true
    const inCoda = useRef({});                   // campo -> ultimo valore in attesa
    const timer = useRef({});
    const [salvatoAlle, setSalvatoAlle] = useState({});   // campo -> istante (ms) dell'ultimo salvataggio
    const base = useRef({});                             // campo -> impronta di cio' che c'e' in archivio
    const inVoloValore = useRef({});                     // campo -> impronta del valore in volo
    const chiudendo = useRef(false);
    const uscendo = useRef(false);                       // dentro salvaPrima: come alla chiusura, senza base non si scrive
    const fallito = useRef(new Set());                   // i campi che non sono passati e non sono stati ritentati
    const inAttesa = useRef(new Set());                  // chi aspetta che le richieste in volo finiscano
    const allaChiusura = useRef(null);
    allaChiusura.current = opzioni.allaChiusura || null;
    const bloccato = useRef(null);
    bloccato.current = opzioni.bloccato || null;
    const occupato = () => Object.values(inVolo.current).some(Boolean) || Object.keys(inCoda.current).length > 0;
    const avvisaFine = () => { [...inAttesa.current].forEach((f) => f()); };

    const segna = (campo, valore) => setStato((p) => ({ ...p, [campo]: valore }));

    const esegui = useCallback(async (campo, valore) => {
        inVolo.current[campo] = true;
        inVoloValore.current[campo] = impronta(valore);
        segna(campo, 'in_corso');
        try {
            await scrivi(campo, valore);
            base.current[campo] = impronta(valore);
            fallito.current.delete(campo);
            /* Se mentre andava e' arrivato un valore nuovo, lo stato
               giusto non e' «salvato»: c'e' ancora da scrivere. */
            if (!(campo in inCoda.current)) {
                segna(campo, 'salvato');
                setSalvatoAlle((p) => ({ ...p, [campo]: Date.now() }));
                if (onSalvato) onSalvato(campo);
                clearTimeout(timer.current[campo]);
                timer.current[campo] = setTimeout(
                    () => setStato((p) => (p[campo] === 'salvato' ? { ...p, [campo]: undefined } : p)),
                    3000);
            }
        } catch (e) {
            segna(campo, 'non_salvato');
            fallito.current.add(campo);
            if (chiudendo.current) {
                /* La schermata non c'e' piu': il segno rosso non lo vede
                   nessuno. Lo dice una notifica, che sta sulla radice. */
                notificaErrore('Una modifica all\'anamnesi non è stata salvata. Riapri la scheda e riscrivila.');
            }
            /* Quello che era in coda si butta: non si riprova da soli, e
               tenerlo vorrebbe dire scrivere piu' tardi un valore che
               chi l'ha scritto crede perso. */
            delete inCoda.current[campo];
            inVolo.current[campo] = false;
            avvisaFine();
            return;
        }
        inVolo.current[campo] = false;
        if (campo in inCoda.current) {
            const prossimo = inCoda.current[campo];
            delete inCoda.current[campo];
            await esegui(campo, prossimo);
        }
        avvisaFine();
    }, [scrivi, onSalvato]);

    /** Da chiamare quando il campo perde il fuoco, o quando cambia una scelta. */
    const salva = useCallback((campo, valore) => {
        const questo = impronta(valore);
        if (inVolo.current[campo]) {
            /* Lo stesso valore che sta gia' andando (Invio e poi l'uscita dal
               campo, lo smontaggio dopo un'uscita): non si scrive due volte. */
            if (inVoloValore.current[campo] === questo && !(campo in inCoda.current)) return;
            inCoda.current[campo] = valore;
            return;
        }
        const conBase = campo in base.current;
        /* Chiusura: senza una base non si sa cosa c'e' in archivio. */
        if ((chiudendo.current || uscendo.current) && !conBase) return;
        if (conBase && base.current[campo] === questo) {
            /* Com'e' in archivio: niente da scrivere. Se era rosso, il campo
               e' tornato uguale a lui e il rosso non ha piu' ragione. */
            fallito.current.delete(campo);
            setStato((p) => (p[campo] === 'non_salvato' ? { ...p, [campo]: undefined } : p));
            return;
        }
        esegui(campo, valore);
    }, [esegui]);

    /** Il valore che c'e' in archivio, dopo averlo letto. */
    const segnaBase = useCallback((campo, valore) => { base.current[campo] = impronta(valore); }, []);

    /* Lo smontaggio: la funzione dell'ultimo render, con `chiudendo` alzato
       perche' `salva` scriva solo i campi con una base e diversi da lei. */
    /** Uscendo da una schermata: manda quello che c'e' da mandare, aspetta le
        richieste in volo e risolve true se e' andato tutto. La scadenza la
        decide chi chiama (utils/uscitaProtetta.js, ATTESA_SALVATAGGIO). */
    const salvaPrima = useCallback(() => new Promise((risolvi) => {
        let puoPartire = true;
        if (allaChiusura.current) {
            uscendo.current = true;
            try { puoPartire = allaChiusura.current() !== false; } finally { uscendo.current = false; }
        }
        if (bloccato.current && bloccato.current()) puoPartire = false;
        const controlla = () => {
            if (occupato()) return;
            inAttesa.current.delete(controlla);
            risolvi(puoPartire && fallito.current.size === 0);
        };
        inAttesa.current.add(controlla);
        controlla();
    }), []);   // eslint-disable-line
    const nonMandato = useCallback(
        () => fallito.current.size > 0 || !!(bloccato.current && bloccato.current()), []);

    useEffect(() => {
        chiudendo.current = false;   // un montaggio di prova (StrictMode) non lascia la bandiera alzata
        return () => {
            if (!allaChiusura.current) return;
            /* «Esci senza salvare», confermato alla domanda: non si riscrive. */
            if (uscitaSenzaSalvareConfermata()) return;
            chiudendo.current = true;   // resta alzata: le risposte arrivano dopo
            allaChiusura.current();
        };
    }, []);

    const nonSalvati = Object.entries(stato).filter(([, v]) => v === 'non_salvato').map(([k]) => k);

    return { stato, salva, nonSalvati, salvatoAlle, segnaBase, uscita: { salvaPrima, nonMandato } };
}
